import React from "react";
import { Form } from "react-final-form";
import { FormattedMessage } from "react-intl";
import { urlLocations } from "routes/urlLocations";
import { Select } from "antd";
import moment from "moment";
import { useDispatch } from "react-redux";

import InputField from "components/InputField/InputField";
import DatePickerField from "components/DatePickerField/DatePickerField";
import SelectField from "components/SelectField/SelectField";
import {
  createLiturgyAction,
  updateLiturgyAction,
} from "services/liturgy/liturgy.action";
import time from "constants/time";
import { RULE_TYPES, EVENT_TYPES } from "constants/constants";
import Styled from "./styled/StyledLiturgyForm";
import StyledCancelLink from "components/Button/styled/StyledCancelLink";
import StyledButtonSubmit from "components/Button/styled/StyledButtonSubmit";
import CheckboxField from "components/CheckboxField/CheckboxField";

const LiturgyForm = ({ id, isCopy, initialValues }) => {
  const { Option } = Select;
  const dispatch = useDispatch();
  const isNew = !id || isCopy;

  const onSubmit = (values) => {
    console.log("values", values);
    const { date, time, publishDate, publishTime, ...rest } = values;

    const formatDateValue = (value) => {
      if (!value) {
        return "";
      }
      if (moment.isMoment(value)) {
        return value.format("YYYY-MM-DD");
      }
      const parsed = moment(value, ["YYYY-MM-DD", moment.ISO_8601], true);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value;
    };

    const formattedDate = formatDateValue(date);
    const formattedPublishDate = formatDateValue(publishDate);

    const isoDate =
      formattedDate &&
      time &&
      moment(`${formattedDate} ${time}`, "YYYY-MM-DD hh:mm A").format();
    const isoPublishDate =
      formattedPublishDate &&
      publishTime &&
      moment(
        `${formattedPublishDate} ${publishTime}`,
        "YYYY-MM-DD hh:mm A"
      ).format();

    if (!!id && !isCopy) {
      dispatch(
        updateLiturgyAction({
          date: isoDate,
          publishDate: isoPublishDate,
          ...rest,
        })
      );
    } else {
      dispatch(
        createLiturgyAction({
          date: isoDate,
          publishDate: isoPublishDate,
          ...rest,
        })
      );
    }
  };

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      render={({ handleSubmit, pristine, submitting, form }) => (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <Styled.LiturgyForm>
            <Styled.FieldsWrap>
              <Styled.Field50>
                <InputField
                  name="title"
                  placeholder="Liturgies.Form.Title"
                  label="Liturgies.Form.Title"
                />
              </Styled.Field50>
              <Styled.Field50>
                <InputField
                  name="titleAr"
                  placeholder="Liturgies.Form.TitleAr"
                  label="Liturgies.Form.TitleAr"
                />
              </Styled.Field50>
              <Styled.Field25>
                <DatePickerField
                  name="date"
                  placeholder="Liturgies.Form.LiturgyDate"
                  label="Liturgies.Form.LiturgyDate"
                />
              </Styled.Field25>
              <Styled.Field25>
                <SelectField
                  name="time"
                  placeholder="Liturgies.Form.Time"
                  label="Liturgies.Form.Time"
                  showSearch
                >
                  {time.map((item) => (
                    <Option value={item} key={item} children={item} />
                  ))}
                </SelectField>
              </Styled.Field25>
              <Styled.Field25>
                <DatePickerField
                  name="publishDate"
                  placeholder="Liturgies.Form.PublishDate"
                  label="Liturgies.Form.PublishDate"
                />
              </Styled.Field25>
              <Styled.Field25>
                <SelectField
                  name="publishTime"
                  placeholder="Liturgies.Form.Time"
                  label="Liturgies.Form.Time"
                >
                  {time.map((item) => (
                    <Option value={item} key={item} children={item} />
                  ))}
                </SelectField>
              </Styled.Field25>
              <Styled.Field25>
                <InputField
                  type="number"
                  name="maxCapacity"
                  placeholder="Liturgies.Form.MaxCapacity"
                  label="Liturgies.Form.MaxCapacity"
                />
              </Styled.Field25>
              <Styled.Field25>
                <InputField
                  type="number"
                  name="bookedCount"
                  placeholder="Liturgies.Form.BookedCapacity"
                  label="Liturgies.Form.BookedCapacity"
                />
              </Styled.Field25>
              <Styled.Field50>
                <SelectField
                  name="eventType"
                  placeholder="Liturgies.Form.PrayerType"
                  label="Liturgies.Form.PrayerType"
                >
                  {EVENT_TYPES.map((eventType) => (
                    <Option
                      value={eventType.value}
                      key={eventType.value}
                      children={
                        <FormattedMessage
                          id={`Liturgy.EventType.${eventType.title}`}
                        />
                      }
                    />
                  ))}
                </SelectField>
              </Styled.Field50>
              <Styled.Field25>
                <SelectField
                  name="ruleType"
                  placeholder="Liturgies.Form.RuleType"
                  label="Liturgies.Form.RuleType"
                >
                  {RULE_TYPES.map((ruleType) => (
                    <Option
                      value={ruleType}
                      children={ruleType}
                      key={ruleType}
                    />
                  ))}
                </SelectField>
              </Styled.Field25>
              <Styled.Field25>
                <InputField
                  name="ruleWindow"
                  placeholder="Liturgies.Form.RuleWindow"
                  label="Liturgies.Form.RuleWindow"
                  type="number"
                />
              </Styled.Field25>
              <Styled.Field25>
                <InputField
                  type="number"
                  name="minAllowedAge"
                  placeholder="Liturgies.Form.MinAllowedAge"
                  label="Liturgies.Form.MinAllowedAge"
                />
              </Styled.Field25>
              <Styled.Field25>
                <InputField
                  type="number"
                  name="maxAllowedAge"
                  placeholder="Liturgies.Form.MaxAllowedAge"
                  label="Liturgies.Form.MaxAllowedAge"
                />
              </Styled.Field25>
              <Styled.Field50>
                <InputField
                  name="ruleKey"
                  placeholder="Liturgies.Form.RuleKey"
                  label="Liturgies.Form.RuleKey"
                />
              </Styled.Field50>
              <Styled.Field50>
                <div className="flex justify-between items-end h-full px-8">
                  <CheckboxField name="exclusive">
                    <FormattedMessage id="Liturgies.Form.Exclusive" />
                  </CheckboxField>
                  <CheckboxField name="isPrivate">
                    <FormattedMessage id="Liturgies.Form.IsPrivate" />
                  </CheckboxField>
                </div>
              </Styled.Field50>
            </Styled.FieldsWrap>
            <Styled.TopMenu>
              <StyledButtonSubmit
                type="button"
                marginRight="32px"
                width="126px"
                disabled={pristine || submitting}
                onClick={() => {
                  form.submit();
                }}
              >
                <FormattedMessage
                  id={
                    isNew
                      ? "Liturgies.Form.SaveLiturgy"
                      : "Liturgies.Form.UpdateLiturgy"
                  }
                />
              </StyledButtonSubmit>

              <StyledCancelLink width="126px" to={urlLocations.liturgies}>
                <FormattedMessage id="Liturgies.Form.Cancel" />
              </StyledCancelLink>
            </Styled.TopMenu>
          </Styled.LiturgyForm>
        </form>
      )}
    />
  );
};

export default LiturgyForm;
